import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Alert,
    Box,
    ButtonGroup,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Tooltip,
    Typography
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    ChevronRight as ChevronRightIcon,
    ExpandMore as ExpandMoreIcon,
    FiberManualRecord as RecordIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Monitor as MonitorIcon,
    PlayArrow as PlayIcon,
    Refresh as RefreshIcon,
    Settings as SettingsIcon,
    Stop as StopIcon,
    Videocam as VideocamIcon,
    VolumeOff as VolumeOffIcon,
    VolumeUp as VolumeUpIcon
} from '@mui/icons-material';

import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import VideoPlayer from "../../components/VideoPlayer";

import {getAllBranches, getBranchDetailById} from "../../api/branch/branchApiClient";
import {startMJPEGStream, stopMJPEGStream, stopStream} from "../../api/stream/streamApiClient";
import {getBackendUrl} from "../../utils/config";

const LiveStream = () => {
    const theme = useTheme();
    // Navigation states
    const [currentStage, setCurrentStage] = useState('selection'); // 'selection' or 'monitoring'
    const [selectedCCTVConfig, setSelectedCCTVConfig] = useState(null);

    // Main data states
    const [branchesWithDetails, setBranchesWithDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI states
    const [expandedNodes, setExpandedNodes] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isMuted, setIsMuted] = useState(true);

    // Stream states
    const [activeStreams, setActiveStreams] = useState({});
    const [streamUrl, setStreamUrl] = useState(null);
    const [streamError, setStreamError] = useState(null);

    // Refs for cleanup
    const isUnmountingRef = useRef(false);
    const activeStreamsRef = useRef({});
    const fullscreenContainerRef = useRef(null);

    // Keep activeStreamsRef in sync with activeStreams state
    useEffect(() => {
        activeStreamsRef.current = activeStreams;
    }, [activeStreams]);

    // Enhanced cleanup function to stop all active streams (both MJPEG and HLS)
    const cleanupAllStreams = useCallback(async () => {
        const streams = activeStreamsRef.current;
        const streamIds = Object.values(streams)
            .filter(stream => stream && stream.streamId)
            .map(stream => stream.streamId);

        if (streamIds.length === 0) return;

        console.log('🧹 LiveStream: Cleaning up', streamIds.length, 'active streams...');

        const stopPromises = streamIds.map(async (streamId) => {
            try {
                // Try to stop both MJPEG and HLS streams
                await Promise.allSettled([
                    stopMJPEGStream(streamId).catch(err =>
                        console.warn(`MJPEG stream ${streamId} stop failed:`, err.message)
                    ),
                    stopStream(streamId).catch(err =>
                        console.warn(`HLS stream ${streamId} stop failed:`, err.message)
                    )
                ]);
                console.log(`✅ LiveStream: Stream ${streamId} stopped successfully`);
            } catch (error) {
                console.warn(`❌ LiveStream: Failed to stop stream ${streamId}:`, error.message);
            }
        });

        await Promise.allSettled(stopPromises);

        if (!isUnmountingRef.current) {
            setActiveStreams({});
            setStreamUrl(null);
            setStreamError(null);
        }
    }, []);

    // Load all branches and their details on component mount
    useEffect(() => {
        loadAllData();
    }, []); // Empty dependency array - loadAllData is stable

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            console.log('🧹 LiveStream: Component unmounting, cleaning up streams...');
            isUnmountingRef.current = true;
            cleanupAllStreams();
        };
    }, [cleanupAllStreams]);

    // Listen for fullscreen changes (e.g., when user presses ESC)
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );

            if (!isCurrentlyFullscreen && isFullscreen) {
                // User exited fullscreen (e.g., pressed ESC)
                setIsFullscreen(false);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, [isFullscreen]);

    // Cleanup on tab visibility change (user switches tabs)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                console.log('🧹 LiveStream: Tab hidden, cleaning up streams...');
                cleanupAllStreams();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [cleanupAllStreams]);

    // Enhanced cleanup on browser close/refresh
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            const streams = activeStreamsRef.current;
            const streamIds = Object.values(streams)
                .filter(stream => stream && stream.streamId)
                .map(stream => stream.streamId);

            if (streamIds.length > 0) {
                console.log('🧹 LiveStream: Browser closing, attempting stream cleanup...');
                event.preventDefault();
                event.returnValue = 'You have active streams. Are you sure you want to leave?';

                // Use sendBeacon for synchronous cleanup
                streamIds.forEach((streamId) => {
                    try {
                        // Try to stop both MJPEG and HLS streams synchronously
                        navigator.sendBeacon(
                            `${getBackendUrl()}/api/stream/stop/${streamId}`,
                            JSON.stringify({streamId})
                        );
                        console.log(`🧹 LiveStream: Sent cleanup beacon for stream ${streamId}`);
                    } catch (error) {
                        console.warn(`❌ LiveStream: Failed to send cleanup beacon for stream ${streamId}:`, error);
                    }
                });

                return event.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    const loadAllData = useCallback(async () => {
        setDataLoading(true);
        setError(null);
        
        try {
            console.log('Loading all branches...');
            const branchesResponse = await getAllBranches();
            const branches = branchesResponse.data || [];

            console.log('Loading details for', branches.length, 'branches...');

            const branchDetailsPromises = branches.map(async (branch) => {
                try {
                    const detailResponse = await getBranchDetailById(branch.branchId || branch.id);
                    return {
                        ...branch,
                        details: detailResponse.data,
                        cctvConfigs: detailResponse.data?.cctv || [],
                        totalCameras: (detailResponse.data?.cctv || []).reduce((total, config) =>
                            total + (config.cameras?.length || 0), 0
                        )
                    };
                } catch (error) {
                    console.error(`Error loading details for branch ${branch.branchId}:`, error);
                    return {
                        ...branch,
                        details: null,
                        cctvConfigs: [],
                        totalCameras: 0,
                        error: error.message
                    };
                }
            });

            const branchesWithDetails = await Promise.all(branchDetailsPromises);
            setBranchesWithDetails(branchesWithDetails);

            console.log('Loaded details for all branches:', branchesWithDetails);

        } catch (error) {
            console.error('Error loading branches:', error);
            setError(error.message || 'Failed to load branch data');
        } finally {
            setDataLoading(false);
        }
    }, []);

    const handleCCTVConfigSelect = useCallback(async (cctvConfig, branch) => {
        // Clean up any existing streams when switching configurations
        console.log('🧹 LiveStream: Switching CCTV config, cleaning up existing streams...');
        await cleanupAllStreams();
        
        setSelectedCCTVConfig({
            ...cctvConfig,
            branch: branch,
            cameras: cctvConfig.cameras || []
        });
        setCurrentStage('monitoring');
        setSelectedCamera(null);
        setStreamUrl(null);
        setStreamError(null);
    }, [cleanupAllStreams]);

    const handleStopStream = useCallback(async () => {
        if (!selectedCamera) return;

        const cameraId = selectedCamera.id;
        const stream = activeStreams[cameraId];

        if (!stream) return;

        setLoading(true);

        try {
            console.log('🧹 LiveStream: Stopping stream for camera:', cameraId);

            // Try to stop both MJPEG and HLS streams
            await Promise.allSettled([
                stopMJPEGStream(stream.streamId).catch(err =>
                    console.warn(`MJPEG stream stop failed:`, err.message)
                ),
                stopStream(stream.streamId).catch(err =>
                    console.warn(`HLS stream stop failed:`, err.message)
                )
            ]);

            setActiveStreams(prev => {
                const newStreams = {...prev};
                delete newStreams[cameraId];
                return newStreams;
            });

            setStreamUrl(null);
            setStreamError(null);
            console.log('✅ LiveStream: Stream stopped successfully');
        } catch (error) {
            console.error('❌ LiveStream: Error stopping stream:', error);
            setStreamError(error.message || 'Failed to stop stream');
        } finally {
            setLoading(false);
        }
    }, [selectedCamera, activeStreams]);

    const handleBackToSelection = useCallback(async () => {
        // Stop any active streams when going back
        console.log('🧹 LiveStream: Going back to selection, cleaning up streams...');
        await cleanupAllStreams();
        
        setCurrentStage('selection');
        setSelectedCCTVConfig(null);
        setSelectedCamera(null);
        setStreamUrl(null);
        setStreamError(null);
    }, [cleanupAllStreams]);

    const handleStartStream = useCallback(async () => {
        if (!selectedCamera) return;

        const cameraId = selectedCamera.id;

        // Clean up any existing streams for this camera before starting new one
        if (activeStreams[cameraId]) {
            console.log('🧹 LiveStream: Cleaning up existing stream for camera:', cameraId);
            await handleStopStream();
        }
        
        setLoading(true);
        setStreamError(null);

        try {
            console.log('🚀 LiveStream: Starting MJPEG stream for camera:', cameraId);
            const response = await startMJPEGStream(cameraId);

            if (response && response.streamUrl) {
                const streamData = {
                    streamId: response.streamId,
                    streamUrl: response.streamUrl,
                    status: 'RUNNING',
                    cameraId: cameraId
                };

                setActiveStreams(prev => ({
                    ...prev,
                    [cameraId]: streamData
                }));

                setStreamUrl(response.streamUrl);
                console.log('Stream started successfully:', response.streamUrl);
            }
        } catch (error) {
            console.error('Error starting stream:', error);
            setStreamError(error.message || 'Failed to start stream');
        } finally {
            setLoading(false);
        }
    }, [selectedCamera]);

    const handleCameraSelect = useCallback(async (camera) => {
        const cameraId = camera.cameraId || camera.id;

        // If clicking on the same camera, toggle stream state
        if (selectedCamera && selectedCamera.id === cameraId) {
            const currentStream = activeStreams[cameraId];
            if (currentStream && currentStream.streamId) {
                // Camera is streaming - stop it
                console.log(`Toggling camera ${cameraId}: stopping stream`);
                try {
                    await stopMJPEGStream(currentStream.streamId);
                    setActiveStreams(prev => {
                        const newStreams = {...prev};
                        delete newStreams[cameraId];
                        return newStreams;
                    });
                    setStreamUrl(null);
                } catch (error) {
                    console.warn(`Failed to stop stream:`, error);
                }
            } else {
                // Camera is not streaming - start it
                console.log(`Toggling camera ${cameraId}: starting stream`);
                await handleStartStream();
            }
            return;
        }

        // Stop previous camera's stream when switching to different camera
        if (selectedCamera && selectedCamera.id !== cameraId) {
            console.log(`🧹 LiveStream: Switching cameras: cleaning up stream for camera ${selectedCamera.id}`);
            const currentStream = activeStreams[selectedCamera.id];
            if (currentStream && currentStream.streamId) {
                try {
                    // Try to stop both MJPEG and HLS streams
                    await Promise.allSettled([
                        stopMJPEGStream(currentStream.streamId).catch(err =>
                            console.warn(`MJPEG stream stop failed:`, err.message)
                        ),
                        stopStream(currentStream.streamId).catch(err =>
                            console.warn(`HLS stream stop failed:`, err.message)
                        )
                    ]);
                    setActiveStreams(prev => {
                        const newStreams = {...prev};
                        delete newStreams[selectedCamera.id];
                        return newStreams;
                    });
                } catch (error) {
                    console.warn(`❌ LiveStream: Failed to stop previous stream:`, error);
                }
            }
        }

        setSelectedCamera({
            ...camera,
            id: cameraId
        });

        // Check if new camera already has an active stream
        if (activeStreams[cameraId]) {
            setStreamUrl(activeStreams[cameraId].streamUrl);
        } else {
            setStreamUrl(null);
        }

        setStreamError(null);
    }, [selectedCamera, activeStreams, handleStartStream]);



    const isStreamActive = useCallback((cameraId) => {
        return activeStreams[cameraId]?.status === 'RUNNING';
    }, [activeStreams]);

    const handleNodeToggle = (nodeId) => {
        setExpandedNodes(prev =>
            prev.includes(nodeId)
                ? prev.filter(id => id !== nodeId)
                : [...prev, nodeId]
        );
    };

    const toggleFullscreen = async () => {
        if (!isFullscreen) {
            // Enter fullscreen
            setIsFullscreen(true);

            // Request native fullscreen
            if (fullscreenContainerRef.current) {
                try {
                    if (fullscreenContainerRef.current.requestFullscreen) {
                        await fullscreenContainerRef.current.requestFullscreen();
                    } else if (fullscreenContainerRef.current.webkitRequestFullscreen) {
                        await fullscreenContainerRef.current.webkitRequestFullscreen();
                    } else if (fullscreenContainerRef.current.mozRequestFullScreen) {
                        await fullscreenContainerRef.current.mozRequestFullScreen();
                    } else if (fullscreenContainerRef.current.msRequestFullscreen) {
                        await fullscreenContainerRef.current.msRequestFullscreen();
                    }
                } catch (error) {
                    console.error('Error entering fullscreen:', error);
                }
            }
        } else {
            // Exit fullscreen
            setIsFullscreen(false);

            // Exit native fullscreen
            try {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
            } catch (error) {
                console.error('Error exiting fullscreen:', error);
            }
        }
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    // Helper function to format camera number (601 -> 06, 501 -> 05, etc.)
    const formatCameraNumber = (channel, index) => {
        if (channel) {
            const channelStr = String(channel);
            if (channelStr.length >= 3) {
                // Truncate last TWO digits: 601 -> 6, 501 -> 5
                const truncated = channelStr.slice(0, -2);
                if (truncated.length === 1) {
                    // If result is 1 digit, add 0 in front: 6 -> 06, 5 -> 05
                    return '0' + truncated;
                } else if (truncated.length === 2) {
                    // If result is 2 digits, return as is
                    return truncated;
                }
                return truncated.padStart(2, '0');
            } else if (channelStr.length === 2) {
                // For 2-digit numbers: return as is
                return channelStr;
            } else if (channelStr.length === 1) {
                // For 1-digit numbers: add 0 in front
                return '0' + channelStr;
            }
        }
        return String(index + 1).padStart(2, '0');
    };

    // Render camera grid item for monitoring stage (row layout)
    const renderCameraGridItem = (camera, index) => {
        const cameraId = camera.cameraId || camera.id;
        const isActive = isStreamActive(cameraId);
        const isSelected = selectedCamera?.id === cameraId;

        return (
            <Card
                key={cameraId}
                sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: isSelected ? 3 : 1,
                    borderColor: isSelected ? 'primary.main' : isActive ? 'success.main' : 'divider',
                    backgroundColor: isSelected ? 'primary.50' : 'background.paper',
                    '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 6,
                        borderColor: 'primary.main'
                    },
                    mb: 1.5,
                    display: 'flex',
                    flexDirection: {xs: 'column', sm: 'row'},
                    height: {xs: 'auto', sm: 120},
                    minHeight: {xs: 200, sm: 120}
                }}
                onClick={() => handleCameraSelect(camera)}
            >
                {/* Camera preview area */}
                <Box
                    sx={{
                        width: {xs: '100%', sm: 160},
                        height: {xs: 120, sm: '100%'},
                        backgroundColor: 'black',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        borderRadius: {xs: '4px 4px 0 0', sm: '4px 0 0 4px'},
                        flexShrink: 0
                    }}
                >
                    <Box sx={{textAlign: 'center', position: 'relative'}}>
                        {/* Unified Camera Icon Container - Shows Play/Stop based on state */}
                        <Box
                            sx={{
                                width: {xs: 48, sm: 44},
                                height: {xs: 48, sm: 44},
                                borderRadius: '50%',
                                background: isSelected && isActive
                                    ? 'linear-gradient(135deg, #f5576c 0%, #ff6b6b 100%)' // Red for stop
                                    : isSelected
                                        ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.focus} 100%)` // Blue for selected
                                        : isActive
                                            ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' // Pink for active
                                            : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Cyan for inactive
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                mb: 1,
                                position: 'relative',
                                boxShadow: isSelected
                                    ? '0 8px 32px rgba(102, 126, 234, 0.4)'
                                    : '0 4px 20px rgba(79, 172, 254, 0.3)',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                    boxShadow: isSelected
                                        ? '0 12px 40px rgba(102, 126, 234, 0.6)'
                                        : '0 8px 32px rgba(79, 172, 254, 0.5)'
                                }
                            }}
                        >
                            {/* Show Play or Stop icon based on state */}
                            {isSelected ? (
                                // Selected camera shows Play or Stop based on streaming state
                                isActive ? (
                                    <StopIcon sx={{
                                        fontSize: {xs: 24, sm: 22},
                                        color: 'white',
                                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                                    }}/>
                                ) : (
                                    <PlayIcon sx={{
                                        fontSize: {xs: 24, sm: 22},
                                        color: 'white',
                                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                                        ml: 0.5 // Slight offset to center visually
                                    }}/>
                                )
                            ) : (
                                // Non-selected cameras show camera icon
                                <VideocamIcon sx={{
                                    fontSize: {xs: 24, sm: 22},
                                    color: 'white',
                                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                                }}/>
                            )}

                            {/* Pulse animation for active cameras */}
                            {isActive && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '50%',
                                        border: '2px solid rgba(245, 87, 108, 0.6)',
                                        animation: 'cameraActivePulse 2s infinite',
                                        '@keyframes cameraActivePulse': {
                                            '0%': {
                                                transform: 'scale(1)',
                                                opacity: 1
                                            },
                                            '50%': {
                                                transform: 'scale(1.2)',
                                                opacity: 0.5
                                            },
                                            '100%': {
                                                transform: 'scale(1)',
                                                opacity: 1
                                            }
                                        }
                                    }}
                                />
                            )}
                        </Box>

                        <Typography variant="caption" display="block" sx={{
                            fontSize: {xs: '0.8rem', sm: '0.75rem'},
                            fontWeight: 'bold',
                            color: isSelected ? 'primary.main' : 'text.primary',
                            textShadow: isSelected ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                        }}>
                            CAM {formatCameraNumber(camera.channel, index)}
                        </Typography>
                    </Box>

                    {/* Status indicator - repositioned */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 6,
                            right: 6,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            zIndex: 10
                        }}
                    >
                        {isActive && (
                            <RecordIcon
                                sx={{
                                    color: 'error.main',
                                    fontSize: {xs: 14, sm: 12},
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                        '0%': {opacity: 1},
                                        '50%': {opacity: 0.5},
                                        '100%': {opacity: 1}
                                    }
                                }}
                            />
                        )}
                        <Chip
                            label={isActive ? 'LIVE' : 'OFF'}
                            color={isActive ? 'success' : 'default'}
                            size="small"
                            sx={{
                                height: {xs: 20, sm: 18},
                                fontSize: {xs: '0.7rem', sm: '0.65rem'},
                                fontWeight: 'bold',
                                color: 'white',
                                backgroundColor: isActive ? 'error.main' : 'grey.600',
                                border: '1px solid rgba(255,255,255,0.7)',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                '& .MuiChip-label': {
                                    textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                                }
                            }}
                        />
                    </Box>


                </Box>

                {/* Camera details */}
                <CardContent sx={{
                    flex: 1,
                    p: {xs: 2, sm: 1.5},
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:last-child': {pb: {xs: 2, sm: 1.5}}
                }}>
                    <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{fontSize: {xs: '1rem', sm: '0.9rem'}}}>
                            Camera {camera.channel || (index + 1)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{
                            mb: 0.5,
                            fontSize: {xs: '0.75rem', sm: '0.7rem'}
                        }}>
                            Status: {camera.status || 'Active'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{
                            fontSize: {xs: '0.75rem', sm: '0.7rem'}
                        }}>
                            Channel: {camera.channel || (index + 1)}
                        </Typography>
                    </Box>

                    {/* Action indicator */}
                    <Box sx={{
                        mt: {xs: 1, sm: 0.5},
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <SoftTypography variant="caption" color="primary" fontWeight="medium" sx={{
                            fontSize: {xs: '0.7rem', sm: '0.65rem'}
                        }}>
                            {isSelected
                                ? (isActive ? '● Streaming - Tap to stop' : '● Selected - Tap to start')
                                : 'Tap to select'
                            }
                        </SoftTypography>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            {isSelected && (
                                <Chip
                                    label="Selected"
                                    color="primary"
                                    size="small"
                                    sx={{
                                        height: 18,
                                        fontSize: '0.6rem',
                                        display: {xs: 'none', sm: 'flex'}
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    // Render CCTV config card for selection stage
    const renderCCTVConfigCard = (cctvConfig, branch) => {
        const cameras = cctvConfig.cameras || [];

        return (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={cctvConfig.configId}>
                <Card
                    sx={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 6,
                            borderColor: 'primary.main'
                        },
                        minHeight: 280, // Increased from 240 to accommodate all content
                        height: 'auto', // Allow flexible height
                        border: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    onClick={() => handleCCTVConfigSelect(cctvConfig, branch)}
                >
                    <CardHeader
                        avatar={
                            <Box
                                sx={{
                                    p: 1,
                                    backgroundColor: 'primary.50',
                                    borderRadius: '50%'
                                }}
                            >
                                <SettingsIcon color="primary"/>
                            </Box>
                        }
                        title={
                            <SoftTypography variant="h6" fontWeight="bold" color="dark">
                                {cctvConfig.locationDescription || `CCTV Config ${cctvConfig.configId}`}
                            </SoftTypography>
                        }
                        subheader={
                            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                                <Chip
                                    label={`${cameras.length} cameras`}
                                    size="small"
                                    color="primary"
                                    variant="filled"
                                    icon={<VideocamIcon/>}
                                />
                                <Chip
                                    label={cctvConfig.status || 'ACTIVE'}
                                    size="small"
                                    color={cctvConfig.status === 'ACTIVE' ? 'success' : 'default'}
                                    variant="filled"
                                />
                            </Box>
                        }
                        sx={{pb: 1}}
                    />
                    <CardContent sx={{
                        pt: 0,
                        pb: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        '&:last-child': {pb: 2} // Ensure proper bottom padding
                    }}>
                        <SoftBox sx={{flex: 1}}>
                            <SoftTypography variant="body2" color="dark" fontWeight="bold" gutterBottom>
                                📡 Connection Details
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" fontWeight="regular" sx={{mb: 0.5}}>
                                <strong>Host:</strong> {cctvConfig.host || cctvConfig.ipAddress || 'N/A'}
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" fontWeight="regular" sx={{mb: 0.5}}>
                                <strong>Port:</strong> {cctvConfig.port || 'N/A'}
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" fontWeight="regular" sx={{mb: 0.5}}>
                                <strong>User:</strong> {cctvConfig.userName || 'N/A'}
                            </SoftTypography>
                            {cctvConfig.ddns && (
                                <SoftTypography variant="body2" color="text" fontWeight="regular" sx={{mb: 0.5}}>
                                    <strong>DDNS:</strong> {cctvConfig.ddns}
                                </SoftTypography>
                            )}
                        </SoftBox>

                        <Box sx={{
                            mt: 'auto',
                            pt: 1,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            textAlign: 'center'
                        }}>
                            <Typography variant="caption" color="primary.main" fontWeight="medium" sx={{
                                fontSize: '0.8rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5
                            }}>
                                Click to monitor this CCTV system →
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    // Render branch section for selection stage
    const renderBranchSection = (branch) => {
        const branchNodeId = `branch-${branch.branchId}`;
        const isExpanded = expandedNodes.includes(branchNodeId);

        return (
            <Paper key={branch.branchId} sx={{mb: 3, overflow: 'hidden'}}>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => handleNodeToggle(branchNodeId)}
                        sx={{
                            py: 2,
                            backgroundColor: 'primary.50',
                            '&:hover': {
                                backgroundColor: 'primary.100'
                            }
                        }}
                    >
                        <ListItemIcon sx={{minWidth: 40}}>
                            {isExpanded ? <ExpandMoreIcon/> : <ChevronRightIcon/>}
                        </ListItemIcon>
                        <ListItemIcon sx={{minWidth: 40}}>
                            <BusinessIcon color="primary"/>
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <SoftBox display="flex" alignItems="center" gap={1}>
                                    <SoftTypography variant="h6" fontWeight="bold" color="dark">
                                        {branch.location || branch.name}
                                    </SoftTypography>
                                    <Chip
                                        label={`${branch.cctvConfigs?.length || 0} CCTV configs`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                    />
                                    <Chip
                                        label={`${branch.totalCameras || 0} cameras`}
                                        size="small"
                                        variant="filled"
                                        color="default"
                                    />
                                </SoftBox>
                            }
                            secondary={
                                <SoftTypography variant="body2" color="text" fontWeight="regular" mt={0.5}>
                                    {branch.address || 'No address provided'} •
                                    Phone: {branch.phoneNo || 'N/A'}
                                </SoftTypography>
                            }
                        />
                    </ListItemButton>
                </ListItem>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{p: 3, backgroundColor: 'background.default'}}>
                        {branch.error ? (
                            <Alert severity="error" sx={{mb: 2}}>
                                Failed to load branch details: {branch.error}
                            </Alert>
                        ) : branch.cctvConfigs.length === 0 ? (
                            <SoftTypography color="text" textAlign="center" fontWeight="regular" sx={{py: 2}}>
                                No CCTV configurations found for this branch
                            </SoftTypography>
                        ) : (
                            <Grid container spacing={3}>
                                {branch.cctvConfigs.map(cctvConfig =>
                                    renderCCTVConfigCard(cctvConfig, branch)
                                )}
                            </Grid>
                        )}
                    </Box>
                </Collapse>
            </Paper>
        );
    };

    const totalCameras = branchesWithDetails.reduce((total, branch) => total + branch.totalCameras, 0);

    // Selection Stage - Full screen branch/CCTV selection
    if (currentStage === 'selection') {
        return (
        <DashboardLayout>
            <SoftBox p={3}>
                {/* Header */}
                <SoftBox mb={3}>
                    <SoftBox display="flex" alignItems="center" justifyContent="space-between">
                        <SoftBox>
                            <SoftTypography variant="h5" fontWeight="bold" textGradient gutterBottom>
                                Live Stream
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" fontWeight="regular">
                                Choose a CCTV configuration to monitor • {branchesWithDetails.length} branches
                                • {totalCameras} cameras total
                            </SoftTypography>
                        </SoftBox>
                        <SoftBox display="flex" gap={1} alignItems="center">
                            <SoftButton
                                variant="gradient"
                                color="primary"
                                onClick={loadAllData}
                                disabled={dataLoading}
                                startIcon={<RefreshIcon/>}
                            >
                                Refresh
                            </SoftButton>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>

                {/* Content */}
                {error ? (
                    <Alert severity="error" sx={{mb: 3}}>
                        {error}
                        <SoftButton
                            onClick={loadAllData}
                            size="small"
                            sx={{mt: 1}}
                        >
                            Retry
                        </SoftButton>
                    </Alert>
                ) : dataLoading ? (
                    <SoftBox display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                         minHeight="400px">
                        <CircularProgress size={60} sx={{mb: 2}}/>
                        <SoftTypography variant="h6" color="text" fontWeight="medium" mt={2}>
                            Loading CCTV Network...
                        </SoftTypography>
                    </SoftBox>
                ) : branchesWithDetails.length === 0 ? (
                    <SoftBox textAlign="center" py={8}>
                        <SoftTypography variant="h6" color="text" fontWeight="medium">
                            No branches found
                        </SoftTypography>
                    </SoftBox>
                ) : (
                    <Box>
                        {branchesWithDetails.map(renderBranchSection)}
                    </Box>
                )}
            </SoftBox>
        </DashboardLayout>
    );
    }

    // Monitoring Stage - Full screen video player with camera grid
    return (
        <DashboardLayout>
            <Box
                ref={fullscreenContainerRef}
                sx={{height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column'}}
            >
                {/* Header with back button */}
                <SoftBox p={2} mb={2} sx={{borderRadius: 2, backgroundColor: 'white'}}>
                    <SoftBox display="flex" alignItems="center" gap={2}>
                        <SoftButton
                            variant="outlined"
                            color="dark"
                            onClick={handleBackToSelection}
                            size="small"
                            sx={{minWidth: 'auto', p: 1}}
                        >
                            <ArrowBackIcon/>
                        </SoftButton>
                        <MonitorIcon color="primary" sx={{fontSize: 28}}/>
                        <SoftBox flex={1}>
                            <SoftTypography variant="h5" fontWeight="bold" textGradient>
                                {selectedCCTVConfig?.locationDescription || 'CCTV Monitoring'}
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" fontWeight="regular" mt={0.5}>
                                {selectedCCTVConfig?.branch?.location} • {selectedCCTVConfig?.host}:{selectedCCTVConfig?.port} • {selectedCCTVConfig?.cameras?.length} cameras
                            </SoftTypography>
                        </SoftBox>
                        <SoftBox display="flex" gap={1}>
                            <SoftButton
                                variant="outlined"
                                color="primary"
                                onClick={toggleFullscreen}
                                size="small"
                                sx={{minWidth: 'auto', p: 1}}
                            >
                                {isFullscreen ? <FullscreenExitIcon/> : <FullscreenIcon/>}
                            </SoftButton>
                            <SoftButton
                                variant="gradient"
                                color="primary"
                                onClick={loadAllData}
                                size="small"
                                sx={{minWidth: 'auto', p: 1}}
                            >
                                <RefreshIcon/>
                            </SoftButton>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>

                <Box sx={{
                    display: 'flex',
                    flexDirection: {xs: 'column', lg: 'row'},
                    gap: 2,
                    flex: 1,
                    height: '100%'
                }}>
                    {/* Camera Selection List */}
                    <Paper sx={{
                        width: {xs: '100%', lg: isFullscreen ? '25%' : '30%'},
                        height: {xs: '40vh', lg: '100%'},
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box sx={{p: {xs: 1.5, sm: 2}, borderBottom: 1, borderColor: 'divider'}}>
                            <Typography variant="h6" fontWeight="medium" sx={{fontSize: {xs: '1.1rem', sm: '1.25rem'}}}>
                                Cameras ({selectedCCTVConfig?.cameras?.length || 0})
                            </Typography>
                            <Typography variant="caption" color="text.secondary"
                                        sx={{fontSize: {xs: '0.7rem', sm: '0.75rem'}}}>
                                Tap camera to monitor • Main player will show live stream
                            </Typography>
                        </Box>
                        <Box sx={{flex: 1, overflow: 'auto', p: {xs: 1, sm: 1.5}}}>
                            {selectedCCTVConfig?.cameras?.length > 0 ? (
                                <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                    {selectedCCTVConfig.cameras.map((camera, index) => renderCameraGridItem(camera, index))}
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: {xs: 3, sm: 4},
                                        color: 'text.secondary',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <VideocamIcon sx={{fontSize: {xs: 40, sm: 48}, mb: 2, opacity: 0.5}}/>
                                    <Typography variant="body2" sx={{fontSize: {xs: '0.9rem', sm: '1rem'}}}>
                                        No cameras configured for this CCTV system
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled"
                                                sx={{mt: 1, fontSize: {xs: '0.7rem', sm: '0.75rem'}}}>
                                        Contact your administrator to add cameras
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>

                    {/* Main Video Display */}
                    <Paper sx={{
                        flex: 1,
                        height: {xs: '60vh', lg: '100%'},
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        gap: 0  // Remove any gaps between video and controls
                    }}>
                        {/* Video Player with proper aspect ratio */}
                        <Box sx={{
                            flex: selectedCamera && streamUrl ? '0 1 auto' : 1, // Don't grow when video is playing
                            backgroundColor: 'black',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: selectedCamera && streamUrl ? 0 : '300px',  // Minimum height when no video
                            overflow: 'hidden'
                        }}>
                            {/* Aspect ratio container for video content */}
                            <Box sx={{
                                width: '100%',
                                height: '100%',
                                position: 'relative',
                                backgroundColor: 'black',
                                overflow: 'hidden',
                                // Maintain 16:9 aspect ratio when streaming
                                ...(selectedCamera && streamUrl && {
                                    aspectRatio: '16/9',
                                    maxHeight: {
                                        xs: 'calc((100vw - 32px) * 9 / 16)', // Account for padding on mobile
                                        lg: 'calc((70vw) * 9 / 16)' // 70% of viewport width for desktop
                                    },
                                    margin: 'auto'
                                })
                            }}>
                                {!selectedCamera ? (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        height="100%"
                                        sx={{
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.focus} 100%)`,
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Animated background elements */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: 200,
                                                height: 200,
                                                borderRadius: '50%',
                                                background: 'rgba(255,255,255,0.1)',
                                                top: -100,
                                                right: -100,
                                                animation: 'float 6s ease-in-out infinite',
                                                '@keyframes float': {
                                                    '0%, 100%': {transform: 'translateY(0px)'},
                                                    '50%': {transform: 'translateY(-20px)'}
                                                }
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: 150,
                                                height: 150,
                                                borderRadius: '50%',
                                                background: 'rgba(255,255,255,0.05)',
                                                bottom: -75,
                                                left: -75,
                                                animation: 'float 8s ease-in-out infinite reverse'
                                            }}
                                        />

                                        {/* Large colorful camera icon */}
                                        <Box
                                            sx={{
                                                width: 120,
                                                height: 120,
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 3,
                                                boxShadow: '0 20px 60px rgba(79, 172, 254, 0.4)',
                                                animation: 'pulse 3s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%, 100%': {transform: 'scale(1)'},
                                                    '50%': {transform: 'scale(1.05)'}
                                                }
                                            }}
                                        >
                                            <VideocamIcon sx={{fontSize: 60, color: 'white'}}/>
                                        </Box>

                                        <Typography variant="h4" gutterBottom sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                                        }}>
                                            Select a Camera
                                        </Typography>
                                        <Typography variant="h6" sx={{
                                            color: 'rgba(255,255,255,0.9)',
                                            textAlign: 'center',
                                            maxWidth: 400,
                                            lineHeight: 1.5
                                        }}>
                                            Choose a camera from the colorful grid to start live monitoring
                                        </Typography>
                                    </Box>
                                ) : streamError ? (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        height="100%"
                                        color="white"
                                    >
                                        <Alert severity="error" sx={{mb: 2, maxWidth: 400}}>
                                            {streamError}
                                        </Alert>
                                        <SoftButton
                                            onClick={handleStartStream}
                                            disabled={loading}
                                            startIcon={<RefreshIcon/>}
                                        >
                                            Retry Connection
                                        </SoftButton>
                                    </Box>
                                ) : streamUrl ? (
                                    <VideoPlayer
                                        cameraId={selectedCamera.id}
                                        streamUrl={streamUrl}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain'
                                        }}
                                        muted={isMuted}
                                        onError={(error) => {
                                            console.error('Video player error:', error);
                                            setStreamError('Stream playback failed');
                                        }}
                                    />
                                ) : loading ? (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        height="100%"
                                        sx={{
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.focus} 100%)`,
                                            color: 'white'
                                        }}
                                    >
                                        <CircularProgress size={60} sx={{mb: 2, color: 'white'}}/>
                                        <Typography variant="h6" sx={{textAlign: 'center'}}>
                                            Connecting to Camera {selectedCamera.channel || selectedCamera.id}...
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        height="100%"
                                        sx={{
                                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Animated background elements */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: 300,
                                                height: 300,
                                                borderRadius: '50%',
                                                background: 'rgba(255,255,255,0.1)',
                                                top: -150,
                                                right: -150,
                                                animation: 'float 8s ease-in-out infinite'
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: 200,
                                                height: 200,
                                                borderRadius: '50%',
                                                background: 'rgba(255,255,255,0.05)',
                                                bottom: -100,
                                                left: -100,
                                                animation: 'float 10s ease-in-out infinite reverse'
                                            }}
                                        />

                                        {/* Camera info */}
                                        <Typography variant="h4" gutterBottom sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                            mb: 1
                                        }}>
                                            CAM {formatCameraNumber(selectedCamera.channel, selectedCamera.id)}
                                        </Typography>

                                        <Typography variant="h6" sx={{
                                            color: 'rgba(255,255,255,0.9)',
                                            textAlign: 'center',
                                            mb: 4,
                                            fontWeight: 500
                                        }}>
                                            Ready to stream
                                        </Typography>

                                        {/* YouTube-style play button */}
                                        <Box
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(255,255,255,0.9)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                                                '&:hover': {
                                                    transform: 'scale(1.1)',
                                                    backgroundColor: 'white',
                                                    boxShadow: '0 12px 40px rgba(0,0,0,0.4)'
                                                },
                                                '&:active': {
                                                    transform: 'scale(0.95)'
                                                }
                                            }}
                                            onClick={handleStartStream}
                                        >
                                            <PlayIcon sx={{
                                                fontSize: 40,
                                                color: '#4facfe',
                                                ml: 0.5 // Slight offset to center visually
                                            }}/>
                                        </Box>

                                        <Typography variant="body1" sx={{
                                            color: 'rgba(255,255,255,0.8)',
                                            textAlign: 'center',
                                            mt: 3,
                                            fontSize: '1.1rem'
                                        }}>
                                            Click to start live streaming
                                        </Typography>
                                    </Box>
                                )}

                                {/* Live indicator overlay - top right */}
                                {streamUrl && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            right: 16,
                                            backgroundColor: 'rgba(0,0,0,0.85)',
                                            color: 'white',
                                            px: 2,
                                            py: 1,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            border: '2px solid rgba(255,255,255,0.2)',
                                            backdropFilter: 'blur(4px)',
                                            zIndex: 20  // Ensure it's visible above video content
                                        }}
                                    >
                                        <RecordIcon
                                            sx={{
                                                color: 'error.main',
                                                fontSize: 14,
                                                animation: 'pulse 2s infinite',
                                                '@keyframes pulse': {
                                                    '0%': {opacity: 1, transform: 'scale(1)'},
                                                    '50%': {opacity: 0.7, transform: 'scale(1.1)'},
                                                    '100%': {opacity: 1, transform: 'scale(1)'}
                                                }
                                            }}
                                        />
                                        <Typography variant="caption" fontWeight="bold" sx={{fontSize: '0.8rem'}}>
                                            LIVE • CAM {formatCameraNumber(selectedCamera?.channel, selectedCamera?.id)}
                                        </Typography>
                                    </Box>
                                )}

                                {/* YouTube-style center button - conditional play/stop when streaming */}
                                {streamUrl && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            opacity: 0,
                                            transition: 'opacity 0.3s ease',
                                            zIndex: 15,
                                            '&:hover': {opacity: 1}
                                        }}
                                        className="video-overlay-controls"
                                    >
                                        <Box
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(0,0,0,0.8)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                border: '3px solid rgba(255,255,255,0.9)',
                                                backdropFilter: 'blur(10px)',
                                                '&:hover': {
                                                    transform: 'scale(1.1)',
                                                    backgroundColor: 'rgba(0,0,0,0.9)',
                                                    borderColor: 'white'
                                                },
                                                '&:active': {
                                                    transform: 'scale(0.95)'
                                                }
                                            }}
                                            onClick={handleStopStream}
                                        >
                                            <StopIcon sx={{
                                                fontSize: 40,
                                                color: 'white'
                                            }}/>
                                        </Box>
                                    </Box>
                                )}

                                {/* Make video area show controls on hover */}
                                {streamUrl && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            '&:hover .video-overlay-controls': {
                                                opacity: 1
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Video Controls (mobile-friendly) */}
                        {selectedCamera && (
                            <Box
                                sx={{
                                    p: {xs: 1.5, sm: 2},
                                    backgroundColor: 'rgba(0,0,0,0.9)',
                                    color: 'white',
                                    display: 'flex',
                                    flexDirection: {xs: 'column', sm: 'row'},
                                    alignItems: {xs: 'stretch', sm: 'center'},
                                    justifyContent: {xs: 'center', sm: 'space-between'},
                                    gap: {xs: 1.5, sm: 2},
                                    mt: 0,  // Remove top margin
                                    borderTop: 'none'  // Remove any border that might create separation
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: {xs: 'column', sm: 'row'},
                                    alignItems: {xs: 'center', sm: 'center'},
                                    gap: {xs: 1.5, sm: 2},
                                    width: {xs: '100%', sm: 'auto'}
                                }}>
                                    {/* Play/Stop button like video player */}
                                    <ButtonGroup
                                        variant="contained"
                                        sx={{
                                            width: {xs: '100%', sm: 'auto'},
                                            justifyContent: {xs: 'center', sm: 'flex-start'}
                                        }}
                                    >
                                        {isStreamActive(selectedCamera.id) ? (
                                            <IconButton
                                                onClick={handleStopStream}
                                                disabled={loading}
                                                sx={{
                                                    backgroundColor: 'error.main',
                                                    color: 'white',
                                                    '&:hover': {backgroundColor: 'error.dark'},
                                                    minWidth: {xs: 50, sm: 40},
                                                    flex: {xs: 1, sm: 'none'}
                                                }}
                                            >
                                                <StopIcon/>
                                            </IconButton>
                                        ) : (
                                            <IconButton
                                                onClick={handleStartStream}
                                                disabled={loading}
                                                sx={{
                                                    backgroundColor: 'success.main',
                                                    color: 'white',
                                                    '&:hover': {backgroundColor: 'success.dark'},
                                                    minWidth: {xs: 50, sm: 40},
                                                    flex: {xs: 1, sm: 'none'}
                                                }}
                                            >
                                                <PlayIcon/>
                                            </IconButton>
                                        )}
                                        <IconButton
                                            onClick={toggleMute}
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                '&:hover': {backgroundColor: 'primary.dark'},
                                                minWidth: {xs: 50, sm: 40},
                                                flex: {xs: 1, sm: 'none'}
                                            }}
                                        >
                                            {isMuted ? <VolumeOffIcon/> : <VolumeUpIcon/>}
                                        </IconButton>
                                    </ButtonGroup>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: {xs: 'center', sm: 'left'},
                                            fontSize: {xs: '0.9rem', sm: '0.875rem'}
                                        }}
                                    >
                                        Camera {selectedCamera.channel || selectedCamera.id}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: {xs: 'row', sm: 'row'},
                                    alignItems: 'center',
                                    justifyContent: {xs: 'space-between', sm: 'flex-end'},
                                    gap: {xs: 1, sm: 2},
                                    width: {xs: '100%', sm: 'auto'}
                                }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: {xs: '0.7rem', sm: '0.75rem'}
                                        }}
                                    >
                                        {new Date().toLocaleTimeString()}
                                    </Typography>
                                    <Chip
                                        label={isStreamActive(selectedCamera.id) ? 'STREAMING' : 'STOPPED'}
                                        color={isStreamActive(selectedCamera.id) ? 'success' : 'default'}
                                        size="small"
                                        sx={{
                                            fontSize: {xs: '0.6rem', sm: '0.7rem'},
                                            height: {xs: 20, sm: 24}
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Box>
        </DashboardLayout>
    );
};

export default LiveStream;